import dayjs from 'dayjs';
import axios from 'axios';
import i18n from '@/lang';

export const formatDate = (date = new Date(), type = 'YYYY-MM-DD') => {
  return dayjs(date).format(type);
};

export const base64toFile = (data, fileName) => {
  const dataArr = data.split(',');
  const byteString = atob(dataArr[1]);
  const options = {
    type: 'image/jpeg',
    endings: 'native'
  };
  const u8Arr = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    u8Arr[i] = byteString.charCodeAt(i);
  }
  return new File([u8Arr], `${fileName}.jpg`, options); // 返回文件流
};

export const urlDownload = url => {
  var a = document.createElement('a');
  a.setAttribute('href', url);
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export function fileDown(url, filename, callback) {
  return new Promise((resolve, reject) => {
    axios(url, {
      method: 'get',
      responseType: 'blob',
      onDownloadProgress: evt => {
        if (typeof callback === 'function') {
          const progress = parseInt((evt.loaded / evt.total) * 100);
          callback(progress);
        }
      }
    })
      .then(res => {
        const blob = new Blob([res.data]);
        const a = document.createElement('a');
        a.download = filename;
        a.href = URL.createObjectURL(blob);
        a.click();
        URL.revokeObjectURL(a.href);
        a.remove();
        resolve();
      })
      .catch(err => {
        reject(err);
      });
  });
}

export const formatKeyword = data => {
  return data?.trim().replace(/,/g, ' | ').replace(/，/g, ' | ') || '';
};

// 获取url上参数
export function getParameterByName(name, url) {
  if (!url) url = window.location.href;
  name = name.replace(/[\[\]]/g, '\\$&');
  var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

// 每隔几天触发一次
export const triggerEveryNdays = (n, callback) => {
  const DAY_IN_MS = 24 * 60 * 60 * 1000; // 一天的毫秒数
  const interval = n * DAY_IN_MS; // 30 天的毫秒数

  // 获取上次触发时间
  let lastTriggerTime = localStorage.getItem('lastTriggerTime');

  // 检查是否已经过去了n天
  if (lastTriggerTime) {
    lastTriggerTime = parseInt(lastTriggerTime, 10);
    const currentTime = Date.now();
    if (currentTime - lastTriggerTime >= interval) {
      // 触发回调
      callback();
      // 更新上次触发时间
      localStorage.setItem('lastTriggerTime', currentTime);
    }
  } else {
    // 第一次运行时记录当前时间
    localStorage.setItem('lastTriggerTime', Date.now());
  }
};

// 米转英寸
export const formatM2F = (meter, fixed = 0) => {
  let ft = parseFloat(meter || 0) / 0.3048;
  return ft.toFixed(fixed);
};

// 英寸转米
export const formatF2M = (ft, fixed = 0) => {
  let meter = parseFloat(ft || 0) * 0.3048;
  return meter.toFixed(fixed);
};

// 米面积转英寸面积
export const formatM2Farea = (area, fixed = 0) => {
  let ft = parseFloat(area || 0) * 10.7639;
  return ft.toFixed(fixed);
};

// 英寸面积转米面积
export const formatF2Marea = (ftArea, fixed = 0) => {
  let meter = parseFloat(ftArea || 0) * 0.0929;
  return meter.toFixed(fixed);
};

export const toFixedNoRound = (num = 0, decimalPlaces) => {
  const factor = Math.pow(10, decimalPlaces);
  return Math.floor(num * factor) / factor;
};

// 经纬度十进制格式转DMS格式
export const convertToDMS = (decimal, isLatitude = true) => {
  const degrees = Math.floor(Math.abs(decimal));
  const minutesDecimal = (Math.abs(decimal) - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const secondsDecimal = (minutesDecimal - minutes) * 60; // 高精度秒
  const seconds = Math.round(secondsDecimal); // 最终显示为整数
  const direction = isLatitude ? (decimal >= 0 ? 'N' : 'S') : decimal >= 0 ? 'E' : 'W';

  return {
    dms: `${degrees}°${minutes}'${seconds}"${direction}`,
    source: `${degrees}°${minutes}'${secondsDecimal}"${direction}` // 存储中间变量
  };
};

// 经纬度DMS格式转十进制格式
export const convertDMSToDecimal = dmsString => {
  const regex = /(\d{1,3})°(\d{1,2})'(\d+(?:\.\d+)?)"(N|S|E|W)/g; // 支持小数秒
  const matches = [...dmsString.matchAll(regex)];

  if (matches.length !== 2) {
    throw new Error('Invalid DMS format');
  }

  const convert = dms => {
    const degrees = parseFloat(dms[1]);
    const minutes = parseFloat(dms[2]);
    const seconds = parseFloat(dms[3]);
    const direction = dms[4];

    // 将DMS转换为十进制
    let decimal = degrees + minutes / 60 + seconds / 3600;

    // 如果是南纬或西经，则为负数
    if (direction === 'S' || direction === 'W') {
      decimal = -decimal;
    }

    return toFixedNoRound(decimal, 6);
  };

  // 分别转换纬度和经度
  const latitude = convert(matches[0]);
  const longitude = convert(matches[1]);

  return `${latitude},${longitude}`;
};

export const flattenChildren = input => {
  const result = [];

  const helper = item => {
    result.push(item); // 添加当前项到结果中

    // 如果有 children，则递归处理这些 children
    if (item.children && Array.isArray(item.children)) {
      item.children.forEach(helper); // 递归调用 helper 处理 children
    }
  };

  // 如果输入是数组，逐个处理
  if (Array.isArray(input)) {
    input.forEach(helper);
  } else {
    helper(input); // 如果是单个对象，直接处理
  }

  return result;
};

export const renderName = (array, floorPrefix, additionalFloors) => {
  // 步骤 1: 提取已使用的数字
  const usedNumbers = array
    .map(item => {
      const match = item.match(new RegExp(`${floorPrefix}(\\d+)`)); // 使用动态正则表达式
      return match ? parseInt(match[1], 10) : null;
    })
    .filter(num => num !== null);

  // 步骤 2: 确定未使用的数字
  const maxNumber = Math.max(...usedNumbers, 0); // 获取最大使用的数字
  const newFloors = []; // 存储新的楼层名称

  // 生成新楼层名称
  for (let i = 1; i <= maxNumber + additionalFloors; i++) {
    // 使用额外楼层数量变量
    if (!usedNumbers.includes(i)) {
      newFloors.push(`${floorPrefix}${i}`); // 生成未使用的楼层名称
    }
  }

  return newFloors;
};

export const installMethodList =
  process.env.VUE_APP_CLOUD_DEFAULT_LANGUAGE === 'en'
    ? [
        {
          name: () => i18n.t('newProduct.xiding'),
          value: '0'
        },
        // {
        //   name: () => i18n.t('newProduct.mianban'),
        //   value: '1'
        // },
        {
          name: () => i18n.t('newProduct.bigua'),
          value: '2'
        },
        {
          name: () => i18n.t('newProduct.baogan'),
          value: '3'
        }
      ]
    : [
        {
          name: () => i18n.t('newProduct.xiding'),
          value: '0'
        },
        {
          name: () => i18n.t('newProduct.mianban'),
          value: '1'
        },
        {
          name: () => i18n.t('newProduct.bigua'),
          value: '2'
        },
        {
          name: () => i18n.t('newProduct.baogan'),
          value: '3'
        }
      ];

export const cipherSecret = {
  key: 'PSdiyysfq8m1HVf9',
  iv: 'm3KYNDM9RlQjJyYL'
};
